import React, { useEffect, useRef, useState } from 'react'
import { ArrowRight, UserPlus } from 'lucide-react'
import { useInView } from 'react-intersection-observer'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

const TeamMembers = () => {

    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0,           // ✅ fire as soon as even 1px enters viewport
        rootMargin: '0px 0px -50px 0px', // ✅ trigger slightly before it fully enters
    });

    const container = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    const items = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1 },
    };

    const navigate = useNavigate();

    const [team, setTeam] = useState([]);
    const [ceo, setCeo] = useState(null);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await fetch("https://hifah-technology-official-backend-production.up.railway.app/api/get-all-member");
                const data = await res.json();

                if (data.members && data.members.length > 0) {
                    const reversedMembers = data.members.slice().reverse();
                    setCeo(reversedMembers[0]);
                    setTeam(reversedMembers.slice(1));
                }
            } catch (error) {
                console.error("Error fetching users:", error);
            }
        };

        fetchUsers();
    }, []);

    const isMobile = typeof window !== "undefined" && window.innerWidth < 440;

    // ---------------------------------------------------------------
    // Slider logic: chunk the team array into "pages" (slides).
    // Columns follow the same breakpoints already used on the grid
    // (sm/md: 3 cols, lg+: 4 cols). Rows default to 3 per slide, but
    // step up to 4 rows on narrower screens (fewer columns) so each
    // slide still carries a comparable number of members and the
    // motion feels balanced rather than sparse.
    // ---------------------------------------------------------------
    const [columns, setColumns] = useState(3);
    const [rowsPerSlide, setRowsPerSlide] = useState(3);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [direction, setDirection] = useState(1);
    const intervalRef = useRef(null);

    useEffect(() => {
        const computeGrid = () => {
            const w = window.innerWidth;
            if (w >= 1024) {
                setColumns(4);
                setRowsPerSlide(3);
            } else if (w >= 640) {
                setColumns(3);
                setRowsPerSlide(3);
            } else {
                // mobile: fewer columns, so extend to 4 rows per slide
                setColumns(2);
                setRowsPerSlide(4);
            }
        };

        computeGrid();
        window.addEventListener('resize', computeGrid);
        return () => window.removeEventListener('resize', computeGrid);
    }, []);

    const itemsPerSlide = columns * rowsPerSlide;

    const slides = React.useMemo(() => {
        if (!team.length) return [];
        const chunks = [];
        for (let i = 0; i < team.length; i += itemsPerSlide) {
            const chunk = team.slice(i, i + itemsPerSlide);
            // Pad the final chunk with invisible placeholders so every
            // slide always has the same number of grid cells (itemsPerSlide).
            // This keeps the slide's height fixed instead of shrinking on
            // the last slide when fewer members are left.
            while (chunk.length < itemsPerSlide) {
                chunk.push(null);
            }
            chunks.push(chunk);
        }
        return chunks;
    }, [team, itemsPerSlide]);

    const totalSlides = slides.length;

    // reset to a valid slide whenever the number of slides changes
    // (e.g. on resize when itemsPerSlide changes)
    useEffect(() => {
        if (currentSlide >= totalSlides) {
            setCurrentSlide(0);
        }
    }, [totalSlides, currentSlide]);

    // auto-advance every 5 seconds, but only once the slider has actually
    // scrolled into view — otherwise the timer runs in the background
    // while the section is still off-screen, and the user lands on a
    // slide that's already several pages in instead of the first one.
    useEffect(() => {
        if (!inView || totalSlides <= 1 || isPaused) return;

        intervalRef.current = setInterval(() => {
            setDirection(1);
            setCurrentSlide((prev) => (prev + 1) % totalSlides);
        }, 5000);

        return () => clearInterval(intervalRef.current);
    }, [inView, totalSlides, isPaused]);

    const goToSlide = (idx) => {
        setDirection(idx > currentSlide ? 1 : -1);
        setCurrentSlide(idx);
    };

    const slideVariants = {
        enter: (dir) => ({
            x: dir > 0 ? 60 : -60,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (dir) => ({
            x: dir > 0 ? -60 : 60,
            opacity: 0,
        }),
    };

    const gridColsClass =
        columns === 4
            ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
            : 'grid-cols-2 sm:grid-cols-3';

    return (
        <section className='bg-[#091114] mont overflow-hidden'>
            <div className='w-[88%] sm:w-[88%] mx-auto py-16 pt-14 sm:pt-24'>
                <h2 className="mont text-2xl font-semibold tracking-tight text-[#FFFFFFE5] sm:text-[40px]">
                    Executive Leadership <br />
                    <span className='text-[#A5D283]'>Team Members</span> with role
                </h2>

                <div className='flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center'>
                    <p className="mt-1 sm:mt-4 text-sm sm:text-lg mont font-regular text-[#ffffff95] w-full sm:w-[60%]">
                        Meet our executive leadership and team members, each playing {isMobile ? <></> : <br />}
                        a key role in driving strategy, innovation, and delivering impactful results.
                    </p>
                    <button
                        onClick={() => navigate('/our-team')}
                        className='text-[#A5D283] hidden sm:flex text-sm sm:text-base font-medium items-center gap-2'
                    >
                        See all Members <ArrowRight className='w-4 h-4 sm:w-5 sm:h-5' />
                    </button>
                </div>

                {/* Main Layout: Stack on mobile & tablet, side-by-side on desktop */}
                <div className='flex flex-col lg:flex-row items-center justify-between mt-6 lg:mt-14 gap-8 lg:gap-0'>

                    {/* CEO Card - Full width on mobile & tablet */}
                    {ceo && (
                        <motion.div
                            initial={isMobile ? false : { opacity: 0, x: -60 }}
                            animate={isMobile ? {} : (inView ? { opacity: 1, x: 0 } : {})}
                            transition={isMobile ? { duration: 0 } : { duration: 1.1 }}
                            className='bg-[#A5D28314] text-center w-full lg:w-[35%] rounded-2xl border border-dashed p-6 border-[#A5D28333]'
                        >
                            <div className='flex justify-center'>
                                <div className='w-[180px] h-[180px] sm:w-[221px] sm:h-[221px] rounded-full overflow-hidden flex-shrink-0'>
                                    <img
                                        className='w-full h-full object-cover object-center'
                                        src={ceo.imageUrl}
                                        alt={`${ceo.name} ${ceo.lname}`}
                                    />
                                </div>
                            </div>
                            <p className='font-semibold text-sm sm:text-base text-white mt-4 sm:mt-9'>
                                {ceo.name} {ceo.lname}
                            </p>
                            <p className='font-medium text-xs sm:text-base text-[#FFFFFFB2] mt-3'>
                                {ceo.role}
                            </p>
                            <div className='flex justify-center'>
                                <button
                                    onClick={() => navigate('/hire-developers')}
                                    className='text-[#A5D283] my-0 sm:my-14 mt-6 bg-[#A5D2831F] border border-[#A5D283] rounded-full py-2 px-5 text-sm sm:text-base font-medium flex items-center gap-4'
                                >
                                    <UserPlus className='w-4 h-4 sm:w-5 sm:h-5' /> Hire any team member
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Team Members Slider */}
                    <div
                        ref={ref}
                        className='w-full'
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                    >
                        <div className='overflow-hidden'>
                            <AnimatePresence mode='wait' custom={direction} initial={false}>
                                <motion.div
                                    key={currentSlide}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial='enter'
                                    animate='center'
                                    exit='exit'
                                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                                >
                                    <motion.div
                                        variants={container}
                                        initial={isMobile ? false : "hidden"}
                                        animate={isMobile ? "visible" : inView ? "visible" : "hidden"}
                                        className={`grid ${gridColsClass} gap-4 mt-7 lg:mt-0`}
                                    >
                                        {(slides[currentSlide] || []).map((t, idx) => (
                                            t === null ? (
                                                // invisible spacer: keeps grid row/height count
                                                // consistent even when this slide has fewer members
                                                <div
                                                    key={`placeholder-${idx}`}
                                                    aria-hidden='true'
                                                    className='text-center w-full pointer-events-none opacity-0'
                                                >
                                                    <div className='flex justify-center'>
                                                        <div className='w-[120px] h-[120px] sm:w-[131px] sm:h-[131px] lg:w-[151px] lg:h-[151px] rounded-full' />
                                                    </div>
                                                    <p className='font-semibold text-sm sm:text-base mt-3'>&nbsp;</p>
                                                    <p className='font-regular text-xs sm:text-sm'>&nbsp;</p>
                                                </div>
                                            ) : (
                                                <motion.div
                                                    variants={items}
                                                    transition={{ duration: 0.4, ease: "easeOut" }}
                                                    key={t._id || idx}
                                                    className='text-center w-full'
                                                >
                                                    <div className='flex justify-center'>
                                                        <div className='w-[120px] h-[120px] sm:w-[131px] sm:h-[131px] lg:w-[151px] lg:h-[151px] rounded-full overflow-hidden flex-shrink-0'>
                                                            <img
                                                                className='w-full h-full object-cover object-center'
                                                                src={t.imageUrl}
                                                                alt={t.name}
                                                            />
                                                        </div>
                                                    </div>
                                                    <p className='font-semibold text-sm sm:text-base text-white mt-3'>
                                                        {t.name} {t.lname}
                                                    </p>
                                                    <p className='font-regular text-xs sm:text-sm text-[#FFFFFFB2]'>
                                                        {t.role}
                                                    </p>
                                                </motion.div>
                                            )
                                        ))}
                                    </motion.div>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Slide indicators */}
                        {totalSlides > 1 && (
                            <div className='flex justify-center items-center gap-2 mt-8'>
                                {slides.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => goToSlide(idx)}
                                        aria-label={`Go to slide ${idx + 1}`}
                                        className={`h-2 rounded-full transition-all duration-300 ease-in-out ${
                                            idx === currentSlide
                                                ? 'w-6 bg-[#A5D283]'
                                                : 'w-2 bg-[#A5D28333]'
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <button
                    onClick={() => navigate('/our-team')}
                    className='text-[#A5D283] flex sm:hidden mx-auto mt-7 text-sm sm:text-base font-medium items-center gap-2'
                >
                    See all Members <ArrowRight className='w-4 h-4 sm:w-5 sm:h-5' />
                </button>
            </div>
        </section>
    )
}

export default TeamMembers